// Platform (superadmin only): coupons for tallerza.cl. Created with the code
// as id, turned off instead of deleted (firestore.rules), shared as a link
// the landing reads (?cupon=CODIGO).
import { createFileRoute } from '@tanstack/react-router';
import { Plus, TicketPercent } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useContactos, useCupones } from '@/datos/consultas';
import { exigir } from '@/lib/guardias';
import { useEsEscritorio } from '@/lib/medios';
import { permisos } from '@/lib/sesion';
import { DialogoNuevoCupon } from '@/plataforma/DialogoCupon';
import { ListaCuponesMovil, TablaCupones } from '@/plataforma/ListaCupones';
import { contactosPorCupon } from '@/plataforma/contactos';
import { esVigente, ordenarCupones } from '@/plataforma/cupones';
import { ErrorCarga, Esqueleto, Tarjeta, Vacio } from '@/ui/basicos';
import { Boton } from '@/ui/boton';
import { Contenedor } from '@/ui/pagina';

export const Route = createFileRoute('/_app/plataforma/cupones')({
  beforeLoad: exigir(permisos.plataforma),
  component: Pagina,
});

function Pagina() {
  const q = useCupones();
  const escritorio = useEsEscritorio();
  const [nuevo, setNuevo] = useState({ abierto: false, n: 0 });
  const cupones = useMemo(() => ordenarCupones(q.data ?? []), [q.data]);
  const vigentes = cupones.filter((c) => esVigente(c)).length;
  // The shell already listens to contactos (nav counter): same query key, no
  // extra reads. Until it loads, the column shows only the applied count.
  const contactos = useContactos();
  const traen = useMemo(
    () => (contactos.data ? contactosPorCupon(contactos.data) : undefined),
    [contactos.data],
  );
  const ayudaId = useId();

  const crear = (
    <Boton variante="primario" onClick={() => setNuevo((x) => ({ abierto: true, n: x.n + 1 }))}>
      <Plus /> Nuevo cupón
    </Boton>
  );

  return (
    <Contenedor>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="titulo text-[24px] leading-tight">Cupones</h1>
          <p className="text-[15px] text-text-2">
            {q.data
              ? `${vigentes === 0 ? 'Ningún cupón vigente' : vigentes === 1 ? '1 cupón vigente' : `${vigentes} cupones vigentes`} · códigos para el sitio tallerza.cl`
              : 'Cargando…'}
          </p>
        </div>
        {crear}
      </div>

      {q.isError ? (
        <ErrorCarga error={q.error} />
      ) : !q.data ? (
        <Tarjeta className="flex flex-col gap-3 p-5" aria-busy="true" aria-label="Cargando cupones">
          <Esqueleto className="h-12" />
          <Esqueleto className="h-12" />
        </Tarjeta>
      ) : cupones.length === 0 ? (
        <Tarjeta>
          <Vacio icono={<TicketPercent />} titulo="Aún no hay cupones" accion={crear}>
            Crea un código para una campaña o un cliente referido y comparte su enlace: quien entra
            por él lo ve aplicado en el formulario.
          </Vacio>
        </Tarjeta>
      ) : (
        <Tarjeta className="overflow-hidden">
          {escritorio ? (
            <TablaCupones filas={cupones} traen={traen} ayudaId={ayudaId} />
          ) : (
            <ListaCuponesMovil filas={cupones} traen={traen} />
          )}
        </Tarjeta>
      )}
      <p id={ayudaId} className="text-[13px] text-text-2">
        Aplicados: se descuenta al marcar un contacto como cliente; enviar el formulario del sitio
        con un cupón no lo gasta. Los cupones no se borran: se desactivan, para que los contactos
        que llegaron con uno sigan mostrando su beneficio.
      </p>
      <DialogoNuevoCupon
        key={nuevo.n}
        abierto={nuevo.abierto}
        onCambio={(v) => setNuevo((x) => ({ ...x, abierto: v }))}
      />
    </Contenedor>
  );
}
